import { PhysicalAddress } from "@redotech/customer-portal-sdk/rpc/schema/schemas";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import { RpcClientContext } from "../../contexts/rpc";

export interface AddressChangedFields {
  street1: boolean;
  street2: boolean;
  city: boolean;
  state: boolean;
  zip: boolean;
  country: boolean;
}

function differs(a?: string | null, b?: string | null): boolean {
  return (a ?? "").trim() !== (b ?? "").trim();
}

export function useAddressDiff(
  address: PhysicalAddress | undefined,
  suggestedAddress: PhysicalAddress | undefined,
): { changedFields: AddressChangedFields | undefined; changed: boolean } {
  const changedFields = useMemo(() => {
    if (!address || !suggestedAddress) {
      return undefined;
    }
    return {
      street1: differs(address.street1, suggestedAddress.street1),
      street2: differs(address.street2, suggestedAddress.street2),
      city: differs(address.city, suggestedAddress.city),
      state: differs(address.state, suggestedAddress.state),
      zip: differs(address.zip, suggestedAddress.zip),
      country: differs(address.country, suggestedAddress.country),
    };
  }, [address, suggestedAddress]);

  return {
    changedFields,
    changed: changedFields ? Object.values(changedFields).some(Boolean) : false,
  };
}

export const verifyAddressQueryKey = (address?: PhysicalAddress) =>
  [
    "verifyAddress",
    address?.street1,
    address?.street2,
    address?.city,
    address?.state,
    address?.zip,
    address?.country,
  ] as const;

export const useVerifyAddress = ({
  address,
}: {
  address?: PhysicalAddress;
}) => {
  const client = useContext(RpcClientContext);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: verifyAddressQueryKey(address),
    queryFn: async () => {
      if (!client || !address) {
        throw new Error("Client or address is missing");
      }
      const verifyAddressResponse = await client.verifyAddress({ address });
      const verifiedKey = verifyAddressQueryKey(
        verifyAddressResponse.verifiedAddress,
      );
      queryClient.setQueryData(verifiedKey, verifyAddressResponse);
      return verifyAddressResponse;
    },
    enabled: !!client && !!address,
    staleTime: Infinity,
  });
};
